import type { RefObject } from 'react'
import { Link } from '@tanstack/react-router'
import clsx from 'clsx'
import { LucideIcon } from 'lucide-react'
import { motion } from 'motion/react'

import { usePrefersReducedMotion } from './hooks'

type BottomMenuIconProps = {
  Icon: LucideIcon
  className: string
  isActive: boolean
}

const BottomMenuIcon = ({ Icon, className, isActive }: BottomMenuIconProps) => {
  const prefersReducedMotion = usePrefersReducedMotion()

  if (!isActive) return <Icon className={className} />

  if (prefersReducedMotion) return <Icon className={className} />

  return (
    <motion.span
      animate={{ rotate: [0, -7, 6, -4, 3, 0] }}
      className="inline-flex will-change-transform"
      initial={{ rotate: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <Icon className={className} />
    </motion.span>
  )
}

type MenuItemProps = {
  Icon: LucideIcon
  label: string
  // TODO: pass through typesafe path
  to: string
  id?: string
  ref?: RefObject<HTMLAnchorElement | null>
}

export const MenuItem = ({ Icon, id, label, to, ref }: MenuItemProps) => (
  <Link
    ref={ref}
    activeProps={{ 'aria-current': 'page' }}
    className="group relative z-10 flex min-w-0 flex-1 items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm-landscape:min-h-0 sm-landscape:min-w-0"
    id={id}
    to={to}
  >
    {({ isActive }) => (
      <span className="flex flex-col items-center justify-center gap-0.5 p-1 md:p-2">
        <BottomMenuIcon
          Icon={Icon}
          className={clsx(
            'h-7 w-7 shrink-0 transition-colors',
            isActive
              ? 'stroke-blue-600 dark:stroke-blue-400'
              : 'stroke-slate-500 group-hover:stroke-slate-700 dark:stroke-white/75 dark:group-hover:stroke-white/90',
          )}
          isActive={isActive}
        />
        <span
          className={clsx(
            'max-w-full truncate text-xs transition-colors',
            isActive
              ? 'font-medium text-blue-600 dark:text-blue-400'
              : 'text-slate-500 group-hover:text-slate-700 dark:text-white/75 dark:group-hover:text-white/90',
          )}
        >
          {label}
        </span>
      </span>
    )}
  </Link>
)
